// Mirrors impire-auth/wire/wire.go — the verbs the auth service adds beyond
// the library's identity module, the sign-in record, the provider
// configuration, the identity plane's subjects. JSON field names are the Go
// file's; a shape that differs is a defect here. A subject is built by the
// library, never inlined.

import { identity, subject } from "@impire-io/sdk";

import type { Role } from "./identity.ts";

/** The verbs, as the tokens after the family. */
export const Verb = {
  apps: "apps",
  rows: "rows",
  members: "members",
  tenant: "tenant",
  provider: "provider",
  kick: "kick",
  signOutAll: "sign-out-all",
  signins: "signins",
  settings: "settings",
  plane: "plane",
  children: "children",
  frames: "frames",
  managedProvider: "managed-provider",
} as const;

/** The actions under a verb. */
export const Action = {
  register: "register",
  list: "list",
  get: "get",
  rotate: "rotate",
  revoke: "revoke",
  /** An admin restores a suspended personal app (04-apps.md § re-attestation and suspension). */
  restore: "restore",
  put: "put",
  delete: "delete",
  add: "add",
  create: "create",
  transfer: "transfer",
  detach: "detach",
  issue: "issue",
  invite: "invite",
  use: "use",
} as const;

/** subjectOf is the tokenless form a client writes: `identity.<verb>.<action…>`. */
export function subjectOf(verb: string, ...action: string[]): string {
  return subject.client(subject.Family.identity, verb, ...action);
}

// The subjects each surface speaks, verb and action paired as the service
// serves them (impire-auth/internal/verbs/verbs.go: kick and sign-out-all
// bare, signins under list).
export const Subjects = {
  appsRegister: () => subjectOf(Verb.apps, Action.register),
  appsList: () => subjectOf(Verb.apps, Action.list),
  appsGet: () => subjectOf(Verb.apps, Action.get),
  appsRotate: () => subjectOf(Verb.apps, Action.rotate),
  appsRevoke: () => subjectOf(Verb.apps, Action.revoke),
  appsRestore: () => subjectOf(Verb.apps, Action.restore),
  rowsList: () => subjectOf(Verb.rows, Action.list),
  rowsPut: () => subjectOf(Verb.rows, Action.put),
  rowsDelete: () => subjectOf(Verb.rows, Action.delete),
  membersAdd: () => subjectOf(Verb.members, Action.add),
  membersInvite: () => subjectOf(Verb.members, Action.invite),
  tenantGet: () => subjectOf(Verb.tenant, Action.get),
  providerGet: () => subjectOf(Verb.provider, Action.get),
  providerPut: () => subjectOf(Verb.provider, Action.put),
  providerDelete: () => subjectOf(Verb.provider, Action.delete),
  // The switch between the workspace's own provider and the one it inherits (decision 0055).
  providerUse: () => subjectOf(Verb.provider, Action.use),
  kick: () => subjectOf(Verb.kick),
  signOutAll: () => subjectOf(Verb.signOutAll),
  signinsList: () => subjectOf(Verb.signins, Action.list),
  settingsPut: () => subjectOf(Verb.settings, Action.put),
  // The parent's verbs (impire-auth/wire/relation.go).
  childrenCreate: () => subjectOf(Verb.children, Action.create),
  childrenList: () => subjectOf(Verb.children, Action.list),
  childrenTransfer: (child: string) => subjectOf(Verb.children, Action.transfer, child),
  childrenDetach: (child: string) => subjectOf(Verb.children, Action.detach, child),
  // A solution's page: the frame credential (02-DESIGN/14-solutions/03-the-console.md § the frame's credential).
  framesIssue: (solution: string) => subjectOf(Verb.frames, Action.issue, solution),
  // The managed sign-in, read-only to the workspace's admins (13-blueprints/03-required.md).
  managedProviderGet: () => subjectOf(Verb.managedProvider, Action.get),
  // A root's plan key, presented whole (02-DESIGN/17-plans/01-the-entitlement.md § presenting it).
  entitlementPut: () => identity.entitlementPutSubject(),
} as const;

/**
 * planeSubject is an identity-plane verb: `identity.plane.<lane>.<hash>.<verb>`,
 * reached only by a placement whose template carries the lane and the
 * subject hash (03-signing-in.md § sign-up).
 */
export function planeSubject(lane: string, hash: string, verb: string): string {
  return identity.planeSubject(lane, hash, verb);
}

/** The plane's two verbs, the library's names. */
export const PlaneVerb = {
  memberships: identity.Verb.memberships,
  createTenant: identity.Verb.createTenant,
} as const;

/**
 * planePrefixOf reads the lane and the hash back from the one publish entry
 * the plane template expands, `identity.plane.<lane>.<hash>.>`, as the
 * server reports it in the placement's permissions.
 */
export function planePrefixOf(allow: readonly string[]): { lane: string; hash: string } | undefined {
  return identity.planeOf(allow);
}

/** App kinds. */
export const AppKind = {
  personal: "personal",
  tenant: "tenant",
} as const;

export type AppKind = (typeof AppKind)[keyof typeof AppKind];

/** Principal names who a thing was registered by or placed as: a stable subject and a display that is never a key. */
export interface Principal {
  subject: string;
  display?: string;
}

/** App is a registration as the console sees it: never the credential. */
export interface App {
  id: string;
  tenant: string;
  name: string;
  kind: string;
  registrant: Principal;
  active: boolean;
  /** RFC 3339. */
  created: string;
  /** RFC 3339; the zero time when never rotated. */
  rotated?: string;
  /**
   * RFC 3339: when a personal app's registrant last attested their admission —
   * at registration and at every later placement of theirs in the tenant;
   * absent on a tenant app and on one registered before it was recorded,
   * whose registration counts (04-apps.md § re-attestation and suspension).
   */
  attested?: string;
  /** RFC 3339: when the sweep suspended a personal app whose registrant had not re-attested within the install's window; absent while it is not. */
  suspended?: string;
  /**
   * RFC 3339: the app's last use, the time of its latest exchange at the
   * gateway; absent until the first (04-apps.md § the exchange; hq decision 0072).
   */
  used?: string;
}

/** AppRegisterRequest registers an app; a member registers a personal app, an admin a tenant app. */
export interface AppRegisterRequest {
  name: string;
  kind: string;
}

/** AppCredentialReply carries the credential, shown once, beside the registration. */
export interface AppCredentialReply {
  app: App;
  credential: string;
}

/** AppsListReply lists the apps the caller may see: a member's own, an admin's tenant. */
export interface AppsListReply {
  apps: App[];
}

/** AppRequest names one app. */
export interface AppRequest {
  id: string;
}

/** AppReply is one app. */
export interface AppReply {
  app: App;
}

/** Provider configuration ids: the install-level provider every tenant inherits, and a tenant's own. */
export const ProviderID = {
  platform: "platform",
  own: "own",
  /** The managed provider a managed workspace's steward writes from its blueprint. */
  managed: "managed",
} as const;

/**
 * Row is one mapping row: claim, value, role, under one provider
 * configuration. A pending row is keyed on the provider's handle claim until
 * the person's first sign-in.
 */
export interface Row {
  /** The provider configuration's id: ProviderID.platform or ProviderID.own. */
  provider: string;
  claim: string;
  value: string;
  role: Role;
  pending?: boolean;
  /** A row the workspace's blueprint requires, under the managed provider: added to, never removed. */
  required?: boolean;
  /** The handle a pending row was added by, kept on conversion. */
  display?: string;
  /** RFC 3339. */
  written: string;
  /** A pending row's last invite (impire-hq 02-DESIGN/01-identity/05-invitations.md § the invite on the row). */
  invite?: Invite;
}

/** The outcomes of an invite (impire-sdk-go/identity: InviteSent, InviteNotSent). */
export const InviteOutcome = { sent: "sent", notSent: "not-sent" } as const;

/** The reasons an invite was not sent. */
export const InviteReason = {
  unavailable: "unavailable",
  refused: "refused",
  limit: "limit",
  noAddress: "no-address",
} as const;

/** The three limits an invite is held to. */
export const InviteLimit = { workspace: "workspace", admin: "admin", address: "address" } as const;

/** Invite is a pending row's last invite (impire-sdk-go/identity: Invite). */
export interface Invite {
  outcome: string;
  reason?: string;
  limit?: string;
  /** RFC 3339: when the limit frees. */
  frees?: string;
  /** RFC 3339: when it was sent or refused. */
  at?: string;
  /** The mail provider's id for a sent mail. */
  id?: string;
}

/** MemberInviteRequest names a pending row whose invite is sent again (`identity.members.invite`). */
export interface MemberInviteRequest {
  provider: string;
  claim: string;
  value: string;
}

/**
 * ManagedProviderReply is the managed provider as the auth service holds it
 * (impire-sdk-go/identity: ManagedProviderReply), with its required rows and
 * whether the workspace may keep a provider of its own beside it; config
 * absent where none stands.
 */
export interface ManagedProviderReply {
  config?: ProviderConfig;
  own_provider?: "allowed" | "refused";
  rows?: Row[];
}

/** CODE_MANAGED refuses an admin what the managed sign-in fixes. */
export const CODE_MANAGED = "managed";

/** RowsListReply lists a tenant's rows. */
export interface RowsListReply {
  rows: Row[];
}

/** RowPutRequest writes a row. */
export interface RowPutRequest {
  row: Row;
}

/** RowDeleteRequest removes a row by its identity. */
export interface RowDeleteRequest {
  provider: string;
  claim: string;
  value: string;
}

/** RowReply is one row. */
export interface RowReply {
  row: Row;
}

/** MemberAddRequest adds a member by the platform provider's handle: a pending row. */
export interface MemberAddRequest {
  handle: string;
  role: Role;
}

/**
 * Tenant is the tenant as the platform holds it, read by an admin on
 * `identity.tenant.get`: the slug and the plan the platform set, the
 * relation's fields, the root of its tree, and the plan as people read it —
 * the state, the effective features and limits with their sources — and in a
 * self-hosted install the install's license (02-DESIGN/17-plans/
 * 01-the-entitlement.md § what identity.tenant.get answers). The library's
 * type, so that the console keeps no second copy. The plan is not in the
 * public profile.
 */
export type Tenant = identity.Tenant;

/** The plan as `identity.tenant.get` answers it (the library's). */
export type TenantEntitlement = identity.TenantEntitlement;

/** The install's license, in a self-hosted install (the library's). */
export type InstallLicense = identity.InstallLicense;

/** The registry's features, limits and the sources of an effective value (the library's). */
export const Feature = identity.Feature;
export const Limit = identity.Limit;
export const Source = identity.Source;
export const PlanState = identity.PlanState;
export type PlanState = identity.PlanState;
export const REGISTRY = identity.REGISTRY;
export const NO_BOUND = identity.NO_BOUND;

/** The refusals a plan causes, and those of `identity.entitlement.put` (the library's). */
export const PlanCode = identity.PlanCode;
export const EntitlementCode = identity.EntitlementCode;

/** EntitlementPutRequest carries the plan key as it was issued. */
export type EntitlementPutRequest = identity.EntitlementPutRequest;

// Mirrors impire-auth/wire/relation.go — the relation between workspaces
// (02-DESIGN/12-workspace-hierarchy/01-the-relation.md).

/** The relation's refusals, as the error code a verb answers with. */
export const RelationCode = {
  limit: "limit",
  notAllowed: "not-allowed",
  slugTaken: "slug-taken",
  notFound: "not-found",
  cycle: "cycle",
  outsideReach: "outside-reach",
  singleRoot: "single-root",
  hasChildren: "has-children",
} as const;

/** Parent is the workspace that manages this one, or the root whose plan covers it (the library's). */
export type Parent = identity.Parent;

/**
 * Limits are the values a workspace carries where each is applied.
 * `workspaces` is how many may exist beneath it in all, -1 for no bound, and
 * `beneath` how many do; `storage` is the JetStream disk room in bytes, -1
 * unlimited (the library's TenantLimits).
 */
export type Limits = identity.TenantLimits;

/** ChildrenCreateRequest creates a child: its slug, its display name and its first admin's handle. */
export interface ChildrenCreateRequest {
  slug: string;
  name?: string;
  admin: string;
}

/** Child is one child as its parent's Workspaces page lists it. */
export interface Child {
  slug: string;
  name?: string;
  /** The first admin's handle while that row is pending. */
  pending?: string;
  descendants: number;
  managed: boolean;
  parent?: string;
}

/** ChildReply is one child. */
export interface ChildReply {
  child: Child;
}

/** ChildrenListReply is the caller's children. */
export interface ChildrenListReply {
  children: Child[];
}

/** ChildrenTransferRequest names the new parent: one in the caller's subtree, or the caller's own parent. */
export interface ChildrenTransferRequest {
  parent: string;
}

/** TenantReply is the tenant, answered to an admin and refused to a member. */
export type TenantReply = identity.TenantReply;

/** Organisation is the optional organisation claim: its name and the value it must carry. */
export interface Organisation {
  name: string;
  value: string;
}

/**
 * ProviderConfig is a trust configuration as the auth service keeps it and the
 * console reads and writes it (impire-auth/wire/wire.go). It embeds the
 * provider as a sign-in profile publishes it — the fields flat, as
 * impire-sdk-go/identity.Provider marshals them — and adds what only the auth
 * service reads. It never carries the client secret; client_secret_path names
 * where custody holds it, when one is held.
 */
export interface ProviderConfig {
  /** ProviderID.platform or ProviderID.own. */
  id: string;
  lane: string;
  issuer?: string;
  client_id?: string;
  /** Per surface, where the provider forces separate registrations. */
  client_ids?: Record<string, string>;
  /** What people know the provider by, shown on the sign-in button; never matched. */
  display_name?: string;
  authorization_endpoint?: string;
  device_authorization_endpoint?: string;
  token_endpoint?: string;
  /** Where a sign-out ends the issuer's own session; absent where the issuer advertises none. */
  end_session_endpoint?: string;
  scopes?: string[];
  surfaces?: string[];
  /** public | server-side */
  exchange?: string;
  organisation?: Organisation;
  /** `oid` on Entra; `sub` when empty. */
  subject_claim?: string;
  role_claim?: string;
  handle_claim?: string;
  /** `name` when empty. */
  display_claim?: string;
  /** The github lane's user endpoint. */
  user_endpoint?: string;
  /** Where custody holds the client secret, for a server-side exchange. */
  client_secret_path?: string;
}

/** ProviderPutRequest writes a tenant's own provider; the secret goes to custody. */
export interface ProviderPutRequest {
  config: ProviderConfig;
  client_secret?: string;
}

/**
 * ProviderReply is a tenant's own provider, or none, and which provider the
 * workspace signs in through: one at a time (impire-sdk-go/identity:
 * ProviderReply; decision 0055). `in_use` is a ProviderID.
 */
export interface ProviderReply {
  config?: ProviderConfig;
  in_use?: string;
}

/** PROVIDER_INHERITED names, on the switch, the provider the workspace inherits: Impire's, or the managed one. */
export const PROVIDER_INHERITED = "inherited";

/**
 * ProviderUseRequest switches the workspace to its own provider or back to
 * the one it inherits, proven by the credential of a sign-in through it.
 */
export interface ProviderUseRequest {
  provider: typeof ProviderID.own | typeof PROVIDER_INHERITED;
  credential: string;
}

/**
 * ProviderUseReply is the switch done: the provider in use, the rows carried
 * over as waiting rows on an email, and those that were not. Everyone was
 * signed out.
 */
export interface ProviderUseReply {
  in_use: string;
  carried: Row[];
  not_carried: Row[];
}

/** MAX_DISPLAY_NAME bounds a tenant's or a provider's display name, in characters, as the service does. */
export const MAX_DISPLAY_NAME = 80;

/**
 * validDisplayName checks a name as the service does (wire.DisplayName): one
 * to MAX_DISPLAY_NAME characters once trimmed, none of them a control character.
 */
export function validDisplayName(name: string): boolean {
  const t = name.trim();
  return t !== "" && Array.from(t).length <= MAX_DISPLAY_NAME && !/\p{Cc}/u.test(t);
}

/** SettingsPutRequest changes the tenant's display name — an admin's verb; the slug never changes. */
export interface SettingsPutRequest {
  name: string;
}

/** SettingsReply is the tenant's slug and display name as they stand. */
export interface SettingsReply {
  slug: string;
  name: string;
}

/** KickRequest kicks a principal's connections in the tenant. */
export interface KickRequest {
  subject: string;
}

/** KickReply says how many were kicked. */
export interface KickReply {
  kicked: number;
}

/** SignOutAllReply is the instant the wildcard revocation was dated. */
export interface SignOutAllReply {
  /** RFC 3339. */
  at: string;
}

/** SigninsListRequest asks for the latest sign-in records: a member's own, an admin's tenant. */
export interface SigninsListRequest {
  limit?: number;
}

/** SigninsListReply lists records, oldest first. */
export interface SigninsListReply {
  records: SignInRecord[];
}

/** The headers a sign-in record rides with. */
export const Header = {
  lane: "Impire-Lane",
  class: "Impire-Class",
  reason: "Impire-Reason",
} as const;

/** Connection is what the record keeps of the connection. */
export interface Connection {
  client_id: number;
  server_id: string;
  server_name?: string;
  host?: string;
  name?: string;
  lang?: string;
  version?: string;
  tls_version?: string;
  tls_cipher?: string;
}

/** Counts are bytes and messages in one direction. */
export interface Counts {
  msgs: number;
  bytes: number;
}

/** SignInRecord is the body of one record on `audit.<tenant>.signin.<outcome>`. It never carries a token, a credential or a secret. */
export interface SignInRecord {
  /**
   * The AUDIT stream sequence of the message the record was read from, which
   * identity.signins.list attaches (hq decision 0072): the record's id, in the
   * stream's order. Absent from an older service.
   */
  seq?: number;
  /** RFC 3339. */
  when: string;
  /** placed | refused | ended */
  outcome: string;
  lane?: string;
  /** The slug, or the platform's own token. */
  tenant: string;
  issuer?: string;
  audience?: string;
  /** The issuer and audience as claimed, on a refusal. */
  unverified?: boolean;
  principal?: Principal;
  /** An app's. */
  registrant?: Principal;
  app?: string;
  workload?: string;
  placement?: string;
  role?: string;
  tags?: string[];
  user_key?: string;
  /** RFC 3339. */
  expires?: string;
  connection?: Connection;
  /** On a refusal: claim-shape | platform-state. */
  class?: string;
  /** On a refusal: a reason code. */
  reason?: string;
  end_reason?: string;
  sent?: Counts;
  received?: Counts;
}

/**
 * The codes the service answers with (impire-sdk-go/service: the pattern's
 * own; impire-auth/internal/verbs/tenant.go: a provider verb without the
 * plan's entitlement is `unauthorized` with the message
 * "the plan does not include a provider of the tenant's own").
 */
export const Code = {
  unauthorized: "unauthorized",
  badRequest: "bad-request",
  failed: "failed",
  refused: "refused",
  notFound: "not-found",
  conflict: "conflict",
} as const;

/**
 * The refusal lookup (impire-auth/wire/wire.go: RefusalPath, Refusal; decision
 * 0016): a refused person's own reason, kept under the connect token's inbox
 * token for two minutes and answered once at `/auth/refusal/<inbox>` — the
 * class always, the reason only where the class is claim-shape.
 */
export interface Refusal {
  /** RFC 3339. */
  when: string;
  lane: string;
  /** The tenant as far as resolution reached. */
  tenant?: string;
  /** claim-shape | platform-state */
  class: string;
  /** A reason code, on a claim-shape refusal only. */
  reason?: string;
}

/** refusalPath is the lookup's path for one inbox token. */
export function refusalPath(inbox: string): string {
  return `/auth/refusal/${inbox}`;
}
